import * as React from 'react';

import {
    Nut_Security_Actions_SecurityUpdateSettings as SecuritySettingsConstants,
    Nut_Auth_Mfa_Actions_Authenticate as MfaSettingsConstants,
} from 'nutshell-core/settings-constants';

import {SaveButton} from 'shells/button/save-button/save-button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useGetSetting} from '../../core/use-get-setting';
import {PageContainer} from '../../layout';

import {useUpdateMfaRequiredSetting} from '../mfa/graphql/hooks/use-update-mfa-required-setting';
import {SettingsSidebar} from '../settings-sidebar';
import {useGetRoles} from '../user/roles-permissions/graphql/hooks/use-get-roles';

import {useUpdateSecuritySettings, useGetWorkOsInformation} from './graphql/hooks';

import {
    SecuritySettingsSection,
    MfaSettingsSection,
    SsoSettingsSection,
    DirectorySyncSettingsSection,
} from '.';

import styles from './security-page.module.css';

export function SecurityPage() {
    const [emailAcls, setEmailAcls] = React.useState<string>('');
    const [userSessionLength, setUserSessionLength] = React.useState<number>(0);
    const [requireTwoFactor, setRequireTwoFactor] = React.useState<string>('');

    const {updateSecuritySettings, isLoading: isUpdatingSecuritySettings} =
        useUpdateSecuritySettings();
    const {updateMfaRequiredSetting, isLoading: isUpdatingMfaRequiredSetting} =
        useUpdateMfaRequiredSetting();

    const {user} = useGetSessionUser();
    const {roles, isLoading: isLoadingRoles} = useGetRoles();
    const {workOs, isLoading: isLoadingWorkOsSsoInfo} = useGetWorkOsInformation();

    const {setting: userSessionLengthSetting, isLoading: isLoadingUserSessionLengthSetting} =
        useGetSetting({
            settingName: SecuritySettingsConstants.USER_SESSION_LENGTH_SETTING,
        });
    const {setting: emailAclsSetting, isLoading: isLoadingEmailAclsSetting} = useGetSetting({
        settingName: SecuritySettingsConstants.EMAIL_ACLS_SETTING,
    });

    const {
        setting: mfaRequiredForAllUsersSetting,
        isLoading: isLoadingMfaRequiredForAllUsersSetting,
    } = useGetSetting({
        settingName: MfaSettingsConstants.MFA_REQUIRED_FOR_ALL_USERS_SETTING_NAME,
    });

    React.useEffect(() => {
        if (userSessionLengthSetting) {
            // @ts-expect-error
            setUserSessionLength(parseInt(userSessionLengthSetting.value));
        }
        if (emailAclsSetting && emailAclsSetting.value) {
            setEmailAcls(emailAclsSetting.value);
        }
        if (mfaRequiredForAllUsersSetting && mfaRequiredForAllUsersSetting.value) {
            setRequireTwoFactor(mfaRequiredForAllUsersSetting.value);
        }
    }, [userSessionLengthSetting, emailAclsSetting, mfaRequiredForAllUsersSetting]);

    const saveSecuritySettings = () => {
        updateSecuritySettings({
            emailAcls: emailAcls,
            sessionLength: userSessionLength,
        });
    };

    const saveMfaSettings = () => {
        updateMfaRequiredSetting({
            mfaRequiredForAllUsers: requireTwoFactor,
        });
    };

    return (
        <PageContainer title='Security' whiteBackground={true}>
            <div className='flex gap-16'>
                <StackLegacy spacing={16}>
                    <div className={styles['table-wrapper']}>
                        <LegacySetupTableWrapper
                            title='Security settings'
                            actionItem={
                                <div className='pad-8'>
                                    <SaveButton
                                        variant='save'
                                        size='small'
                                        isSaving={isUpdatingSecuritySettings}
                                        // @ts-expect-error
                                        onClick={saveSecuritySettings}
                                        resetOnComplete={true}
                                    />
                                </div>
                            }
                        >
                            <LegacySetupTableContainer overflow='show'>
                                <SecuritySettingsSection
                                    emailAcls={emailAcls}
                                    setEmailAcls={setEmailAcls}
                                    userSessionLength={userSessionLength}
                                    setUserSessionLength={setUserSessionLength}
                                    isLoading={
                                        isLoadingUserSessionLengthSetting ||
                                        isLoadingEmailAclsSetting
                                    }
                                />
                            </LegacySetupTableContainer>
                        </LegacySetupTableWrapper>
                    </div>
                    <div className={styles['table-wrapper']}>
                        <LegacySetupTableWrapper title='Single sign-on settings' actionItem={null}>
                            <LegacySetupTableContainer overflow='show'>
                                <SsoSettingsSection
                                    sso={workOs.sso}
                                    organizationId={workOs.organizationId}
                                    domainInfo={workOs.domainInfo}
                                    isLoading={isLoadingWorkOsSsoInfo}
                                    user={user}
                                />
                            </LegacySetupTableContainer>
                        </LegacySetupTableWrapper>
                    </div>
                    <div className={styles['table-wrapper']}>
                        <LegacySetupTableWrapper title='Directory sync settings' actionItem={null}>
                            <LegacySetupTableContainer overflow='show'>
                                <DirectorySyncSettingsSection
                                    workOs={workOs}
                                    roles={roles}
                                    isLoading={isLoadingRoles || isLoadingWorkOsSsoInfo}
                                />
                            </LegacySetupTableContainer>
                        </LegacySetupTableWrapper>
                    </div>
                    <div className={styles['table-wrapper']}>
                        <LegacySetupTableWrapper
                            title='Two-factor authentication'
                            actionItem={
                                <div className='pad-8'>
                                    <SaveButton
                                        variant='save'
                                        size='small'
                                        isSaving={isUpdatingMfaRequiredSetting}
                                        // @ts-expect-error
                                        onClick={saveMfaSettings}
                                        resetOnComplete={true}
                                    />
                                </div>
                            }
                        >
                            <LegacySetupTableContainer overflow='show'>
                                <MfaSettingsSection
                                    requireTwoFactor={requireTwoFactor}
                                    setRequireTwoFactor={setRequireTwoFactor}
                                    hasMfaToken={
                                        user && user.hasMfaToken !== undefined
                                            ? user.hasMfaToken
                                            : false
                                    }
                                    isLoading={isLoadingMfaRequiredForAllUsersSetting}
                                />
                            </LegacySetupTableContainer>
                        </LegacySetupTableWrapper>
                    </div>
                </StackLegacy>
                <div className={styles['sidebar-container']}>
                    <SettingsSidebar />
                </div>
            </div>
        </PageContainer>
    );
}
